import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/axios'
import { API_ROUTES } from '@/constants/routes'
import { useAuthStore } from '@/stores/useAuthStore'

export interface Address {
    id: number
    label: string
    department: string
    department_display: string
    province: string
    district: string
    address_line: string
    reference: string
    phone: string
    is_default: boolean
    latitude?: number | null
    longitude?: number | null
    created: string
    updated: string
}

type AddressPayload = Omit<Address, 'id' | 'department_display' | 'created' | 'updated'>

const KEYS = {
    all: ['addresses'] as const,
    list: () => [...KEYS.all, 'list'] as const,
    detail: (id: number) => [...KEYS.all, 'detail', id] as const,
}

export function useAddresses() {
    const { isLogged } = useAuthStore()

    return useQuery({
        queryKey: KEYS.list(),
        queryFn: async () => {
            const { data } = await api.get<{ results: Address[] } | Address[]>(API_ROUTES.addresses)
            return Array.isArray(data) ? data : data.results
        },
        enabled: isLogged,
    })
}

export function useCreateAddress() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: AddressPayload) => {
            const { data } = await api.post<Address>(API_ROUTES.addresses, payload)
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KEYS.all })
        },
    })
}

export function useUpdateAddress() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({ id, ...payload }: AddressPayload & { id: number }) => {
            const { data } = await api.patch<Address>(`${API_ROUTES.addresses}${id}/`, payload)
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KEYS.all })
        },
    })
}

export function useDeleteAddress() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (id: number) => {
            await api.delete(`${API_ROUTES.addresses}${id}/`)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: KEYS.all })
        },
    })
}
